import { logger } from '../utils/logger';

export interface AvailabilityStatus {
  videoEnabled: boolean;
  videoReason: string;
  remaining: number | null;
  quotaTotal: number | null;
  quotaUsed: number | null;
  notionReady?: boolean;
  notionMissing?: {
    token: boolean;
    parent: boolean;
    version: boolean;
  };
}

export async function getAvailability(): Promise<AvailabilityStatus> {
  // 1) Check video availability
  let videoEnabled = false;
  let videoReason = "INIT";
  
  try {
    const videoHealth = await fetch("/api/video/health").then(r => r.json());
    videoEnabled = !!videoHealth?.videoEnabled;
    videoReason = videoHealth?.reason ?? (videoHealth ? "UNKNOWN" : "NO_HEALTH_ENDPOINT");
    logger.info("VIDEO_HEALTH_CHECK", { videoEnabled, videoReason });
  } catch (error) {
    logger.error("VIDEO_HEALTH_CHECK_ERROR", { error });
    videoReason = "HEALTH_CHECK_FAILED";
  }

  // 2) Check quota (optional - server might not have it)
  let remaining: number | null = null;
  let quotaTotal: number | null = null;
  let quotaUsed: number | null = null;
  
  try {
    const quotaData = await fetch("/api/quota").then(r => r.json());
    const total = Number(quotaData?.quota);
    const used = Number(quotaData?.used);
    
    if (Number.isFinite(total)) {
      quotaTotal = total;
    }
    if (Number.isFinite(used)) {
      quotaUsed = used;
    }
    if (Number.isFinite(total) && Number.isFinite(used)) {
      remaining = Math.max(0, total - used);
    }
    
    logger.info("QUOTA_CHECK", { quotaTotal, quotaUsed, remaining });
  } catch (error) {
    logger.warn("QUOTA_CHECK_ERROR", { error });
    // Keep nulls - quota is optional
  }

  return { 
    videoEnabled, 
    videoReason, 
    remaining, 
    quotaTotal, 
    quotaUsed 
  };
}

export async function getNotionHealth(): Promise<{
  notionReady: boolean;
  missing: { token: boolean; parent: boolean; version: boolean };
}> {
  try {
    const health = await fetch("/api/notion/health").then(r => r.json());
    logger.info("NOTION_HEALTH_CHECK", health);
    return {
      notionReady: !!health?.notionReady,
      missing: health?.missing || { token: true, parent: true, version: true }
    };
  } catch (error) {
    logger.error("NOTION_HEALTH_CHECK_ERROR", { error });
    return {
      notionReady: false,
      missing: { token: true, parent: true, version: true }
    };
  }
}